import * as React from 'react';
import PropTypes from 'prop-types';
import { withStyles } from '@material-ui/core/styles';
import { Grid, Typography, Menu, MenuItem, Divider } from '@material-ui/core';
import { Link } from 'react-router-dom';
import ExitToAppIcon from '@material-ui/icons/ExitToApp';
import { Logo } from '../common/Logo';
import { Routes } from '../App';
import AssessmentChecklist from '../Assessments/AssessmentChecklist';
import UserInfoBanner from '../common/UserInfoBanner';

const mountainsSvg = require('../../assets/img/mountains2.svg');

const isDemo = process.env.REACT_APP_THEME === 'demo';

const styles = (theme) => ({
  root: {
    position: 'relative',
    width: '100%',
  },
  body: {
    position: 'absolute',
    zIndex: 5,
    width: '100%',
    height: '100%',
    overflowY: 'auto',
  },
  background1: {
    backgroundImage: `url('${mountainsSvg}')`,
    backgroundSize: 'cover',
    backgroundPosition: 'center',
    width: '100%',
    height: '100%',
    position: 'absolute',
    zIndex: 0,
    top: isDemo ? 72 : 120,
    left: 0,
    opacity: isDemo ? 0.3 : 1,
  },
  background2: {
    background: isDemo
      ? 'linear-gradient(180deg, #FFFFFF 0%, rgba(255, 255, 255, 0) 100%), rgba(134, 240, 255, 0.22)'
      : 'linear-gradient(180deg, #FFFFFF 0%, rgba(255, 255, 255, 0) 100%), rgba(134, 240, 255, 0.65)',
    width: '100%',
    height: '100%',
    position: 'absolute',
    zIndex: 1,
    top: isDemo ? 72 : 120,
    left: 0,
  },
  topBar: {
    background: isDemo ? 'rgba(255, 255, 255, 0.92)' : 'none',
    borderBottom: isDemo ? '1px solid rgba(191, 247, 255, 0.4)' : 'none',
    width: '100%',
    padding: '0 3.125rem',
    position: 'absolute',
    zIndex: 10,
    top: 0,
    left: 0,
    height: isDemo ? 72 : 120,
  },
  content: {
    height: '100%',
    paddingTop: isDemo ? 72 : 120,
  },
  notSure: {
    height: '100%',
    padding: 0,
  },
  icon: {
    fontSize: '1.525rem',
  },
  logout: {
    color: theme.palette.primary.main,
    fontSize: '1.225rem',
    marginLeft: 10,
  },
  logoutContainer: {
    textAlign: 'center',
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
  },
  logoIcon: {
    width: 125,
    height: 45,
  },
  logoProfileInfoWrapper: {
    display: 'flex',
    flexDirection: 'row',
    justifyContent: 'flex-start',
    alignItems: 'center',
    width: isDemo ? 'auto' : '70%',
  },
  navWrapper: {
    position: 'absolute',
    left: '50%',
    top: '50%',
    transform: 'translate(-50%, -50%)',
    display: 'flex',
    alignItems: 'center',
    gap: 4,
  },
  navLink: {
    textDecoration: 'none',
    color: '#7c8a8d',
    fontSize: '0.95rem',
    fontWeight: 600,
    padding: '8px 16px',
    borderRadius: 10,
    whiteSpace: 'nowrap',
    transition: 'background .15s, color .15s',
  },
  navLinkActive: {
    background: '#fff',
    color: '#3aa6b9',
    boxShadow: '0 2px 8px rgba(58, 166, 185, 0.18)',
  },
  logoutButton: {
    background: 'none',
    border: 0,
    cursor: 'pointer',
    '&:focus': {
      outline: 0,
    },
  },
  avatarButton: {
    background: 'none',
    border: 0,
    padding: 0,
    cursor: 'pointer',
    '&:focus': { outline: 0 },
  },
  avatar: {
    width: 36,
    height: 36,
    borderRadius: '50%',
    background: 'linear-gradient(135deg, #BFF7FF, #3aa6b9)',
    color: '#fff',
    fontWeight: 600,
    fontSize: '0.875rem',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    marginRight: 14,
  },
  rightCluster: {
    display: 'flex',
    alignItems: 'center',
  },
});

class AppHeaderFooter extends React.PureComponent {
  static propTypes = {
    classes: PropTypes.any.isRequired,
    children: PropTypes.any.isRequired,
    signOut: PropTypes.func.isRequired,
    location: PropTypes.object,
    user: PropTypes.object,
    profile: PropTypes.object,
    notSure: PropTypes.any,
  };

  state = { menuAnchor: null };

  openMenu = (e) => this.setState({ menuAnchor: e.currentTarget });

  closeMenu = () => this.setState({ menuAnchor: null });

  handleSignOut = () => {
    this.closeMenu();
    this.onSignOutClick();
  };

  onSignOutClick = () => {
    const { signOut, user } = this.props;
    signOut(user);
  };

  // Demo-only: initials for the header avatar. Prefer the user's displayName; if it's a
  // single token (or only a district-style name is present), use its first two letters.
  getInitials = () => {
    const { profile } = this.props;
    const name = (profile && profile.displayName) || '';
    const parts = name
      .trim()
      .split(/\s+/)
      .filter(Boolean);
    if (parts.length >= 2) {
      return `${parts[0][0]}${parts[parts.length - 1][0]}`.toUpperCase();
    }
    if (parts.length === 1) {
      return parts[0].slice(0, 2).toUpperCase();
    }
    return '';
  };

  renderHeader = () => {
    const { classes, location, profile } = this.props;
    const { menuAnchor } = this.state;
    // Built here (not at module load) so the circular App barrel import has resolved Routes.
    const NAV_ITEMS = [
      { label: 'A–F Reports', route: Routes.aToF },
      { label: 'Accountability Weights', route: Routes.weights },
      { label: 'Progress Goals', route: Routes.staar },
      { label: 'Flight Plan', route: Routes.flightPlan },
    ];
    return (
      <Grid
        container
        alignItems="center"
        justify={location.pathname !== '/' ? 'space-between' : 'flex-end'}
        className={classes.topBar}
      >
        {location.pathname !== '/' && (
          <div className={classes.logoProfileInfoWrapper}>
            <Logo logoClassName={classes.logoIcon} />
            {profile && !isDemo && <UserInfoBanner />}
          </div>
        )}
        {isDemo && profile && location.pathname !== '/' && (
          <nav className={classes.navWrapper}>
            {NAV_ITEMS.map((item) => {
              const active = location.pathname.startsWith(`/${item.route}`);
              return (
                <Link
                  key={item.route}
                  to={`/${item.route}`}
                  className={`${classes.navLink}${active ? ` ${classes.navLinkActive}` : ''}`}
                >
                  {item.label}
                </Link>
              );
            })}
          </nav>
        )}
        {!isDemo &&
          profile &&
          (location.pathname.startsWith(`/${Routes.aToF}`) ||
            location.pathname.startsWith(`/${Routes.flightPlan}`)) && <AssessmentChecklist />}
        <div className={isDemo ? classes.rightCluster : undefined}>
          {isDemo &&
            profile &&
            (location.pathname.startsWith(`/${Routes.aToF}`) ||
              location.pathname.startsWith(`/${Routes.flightPlan}`)) && <AssessmentChecklist />}
          {isDemo && profile ? (
            <React.Fragment>
              <button type="button" onClick={this.openMenu} className={classes.avatarButton} aria-label="Account menu">
                <div className={classes.avatar}>{this.getInitials() || '·'}</div>
              </button>
              <Menu
                anchorEl={menuAnchor}
                open={Boolean(menuAnchor)}
                onClose={this.closeMenu}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
                transformOrigin={{ vertical: 'top', horizontal: 'right' }}
                getContentAnchorEl={null}
                PaperProps={{
                  style: {
                    borderRadius: 14,
                    minWidth: 232,
                    marginTop: 6,
                    boxShadow: '0 8px 28px rgba(58,166,185,0.18)',
                    border: '1px solid rgba(191,247,255,0.6)',
                  },
                }}
              >
                <UserInfoBanner inMenu />
                <Divider style={{ margin: '6px 0' }} />
                <MenuItem onClick={this.handleSignOut}>
                  <ExitToAppIcon className={classes.icon} color="primary" style={{ marginRight: 10 }} />
                  Log out
                </MenuItem>
              </Menu>
            </React.Fragment>
          ) : (
            <button type="button" onClick={this.onSignOutClick} className={classes.logoutButton}>
              <Grid item className={classes.logoutContainer}>
                <ExitToAppIcon className={classes.icon} color="primary" />
                <Typography align="center" className={classes.logout}>
                  Logout
                </Typography>
              </Grid>
            </button>
          )}
        </div>
      </Grid>
    );
  };

  render() {
    const {
      classes,
      children,
      notSure,
      // location,
    } = this.props;
    return (
      <div className={classes.root}>
        <div className={classes.background1} />
        <div className={classes.background2} />
        <div className={classes.body}>
          {!notSure && this.renderHeader()}
          <div className={notSure ? classes.notSure : classes.content}>{children}</div>
        </div>
      </div>
    );
  }
}

export default withStyles(styles)(AppHeaderFooter);
